import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { getUserSettingsForUser, updateUserSettingsForUser, getSitesForUser } from '../utils/api';
import { useToast } from '../context/ToastContext';
import { ROLE_FACILITY_MANAGER } from '../utils/roles';

const WEEKDAYS = [
        { label: 'Mon', bit: 1 },
        { label: 'Tue', bit: 2 },
        { label: 'Wed', bit: 4 },
        { label: 'Thu', bit: 8 },
        { label: 'Fri', bit: 16 },
];

const NotificationsModal = ({ isOpen, onClose, targetUser }) => {
        const { showToast } = useToast();
        const [settings, setSettings] = useState(null);
        const [sites, setSites] = useState([]);
        const [saving, setSaving] = useState(false);

        useEffect(() => {
                if (!isOpen || !targetUser) return;
                setSettings(null);
                setSites([]);

                Promise.all([
                        getUserSettingsForUser(targetUser.id),
                        getSitesForUser(targetUser.id),
                ]).then(async ([settingsRes, sitesRes]) => {
                        if (settingsRes.ok) {
                                const data = await settingsRes.json();
                                setSettings({
                                        ...data.data,
                                        subscribed_sites: data.data.subscribed_sites.map(s => s.site_id),
                                });
                        }
                        if (sitesRes.ok) {
                                const data = await sitesRes.json();
                                setSites(data.data || []);
                        }
                }).catch(() => {
                        showToast('error', 'Failed to load notification settings');
                });
        }, [isOpen, targetUser, showToast]);

        const handleSave = async () => {
                if (!settings) return;
                setSaving(true);
                try {
                        const response = await updateUserSettingsForUser(targetUser.id, settings);
                        if (!response.ok) throw new Error('Failed to save settings');
                        onClose();
                        showToast('success', 'Notification settings saved');
                } catch (err) {
                        showToast('error', err.message);
                } finally {
                        setSaving(false);
                }
        };

        const toggleSite = (siteId) => {
                setSettings(prev => {
                        const isSelected = prev.subscribed_sites.includes(siteId);
                        return {
                                ...prev,
                                subscribed_sites: isSelected
                                        ? prev.subscribed_sites.filter(id => id !== siteId)
                                        : [...prev.subscribed_sites, siteId],
                        };
                });
        };

        const showSites = !settings?.out_of_office
                && settings?.email_weekdays > 0
                && targetUser?.role?.id !== ROLE_FACILITY_MANAGER
                && sites.length > 0;

        return (
                <Modal isOpen={isOpen} onClose={onClose}>
                        <h3 className="text-lg font-bold mb-1">Email Notifications</h3>
                        <p className="text-sm text-gray-500 mb-4">{targetUser?.email}</p>
                        {!settings ? (
                                <div className="flex justify-center items-center py-8">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
                                </div>
                        ) : (
                                <div className="space-y-4">
                                        <div className="flex items-center justify-between py-3 border-b border-gray-100">
                                                <div>
                                                        <h4 className="font-semibold text-text-strong">Out of Office</h4>
                                                        <p className="text-sm text-text-weak">Pause all email notifications temporarily</p>
                                                </div>
                                                <button
                                                        onClick={() => setSettings(prev => ({ ...prev, out_of_office: !prev.out_of_office }))}
                                                        data-testid="ns-ooo-toggle"
                                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.out_of_office ? 'bg-brand' : 'bg-gray-300'}`}
                                                >
                                                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.out_of_office ? 'translate-x-6' : 'translate-x-1'}`} />
                                                </button>
                                        </div>
                                        {!settings.out_of_office && (
                                                <div className="py-3 border-b border-gray-100">
                                                        <h4 className="font-semibold text-text-strong mb-1">Email Days</h4>
                                                        <p className="text-sm text-text-weak mb-3">Select which weekdays to receive plant health status.</p>
                                                        <div className="flex gap-2">
                                                                {WEEKDAYS.map(({ label, bit }) => (
                                                                        <button
                                                                                key={bit}
                                                                                type="button"
                                                                                onClick={() => setSettings(prev => ({ ...prev, email_weekdays: prev.email_weekdays ^ bit }))}
                                                                                data-testid="ns-weekday"
                                                                                data-weekday-bit={bit}
                                                                                data-selected={(settings.email_weekdays & bit) ? 'true' : 'false'}
                                                                                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${settings.email_weekdays & bit ? 'bg-brand text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                                                        >
                                                                                {label}
                                                                        </button>
                                                                ))}
                                                        </div>
                                                </div>
                                        )}
                                        {!settings.out_of_office && targetUser?.role?.id === ROLE_FACILITY_MANAGER && (
                                                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                                                        <div>
                                                                <h4 className="font-semibold text-text-strong">Monthly Report</h4>
                                                                <p className="text-sm text-text-weak">Receive a PDF overview of all plants on the 1st of each month</p>
                                                        </div>
                                                        <button
                                                                type="button"
                                                                onClick={() => setSettings(prev => ({ ...prev, monthly_report: !prev.monthly_report }))}
                                                                data-testid="ns-monthly-toggle"
                                                                data-selected={settings.monthly_report ? 'true' : 'false'}
                                                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.monthly_report ? 'bg-brand' : 'bg-gray-300'}`}
                                                        >
                                                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.monthly_report ? 'translate-x-6' : 'translate-x-1'}`} />
                                                        </button>
                                                </div>
                                        )}
                                        {showSites && (
                                                <div className="py-3 border-b border-gray-100">
                                                        <h4 className="font-semibold text-text-strong">Subscribed Sites</h4>
                                                        <p className="text-sm text-text-weak mb-2">Select sites to include in the report.</p>
                                                        <div className="space-y-2">
                                                                {sites.map(site => (
                                                                        <div key={site.id} className="flex items-center">
                                                                                <input
                                                                                        type="checkbox"
                                                                                        id={`ns-site-${site.id}`}
                                                                                data-testid="ns-site"
                                                                                data-site-id={site.id}
                                                                                        checked={settings.subscribed_sites.includes(site.id)}
                                                                                        onChange={() => toggleSite(site.id)}
                                                                                        className="h-4 w-4 text-brand border-gray-300 rounded focus:ring-brand"
                                                                                />
                                                                                <label htmlFor={`ns-site-${site.id}`} className="ml-2 text-sm text-text-strong">{site.name}</label>
                                                                        </div>
                                                                ))}
                                                        </div>
                                                </div>
                                        )}
                                        <div className="mt-6 flex justify-end space-x-4">
                                                <button
                                                        type="button"
                                                        onClick={onClose}
                                                        data-testid="ns-cancel"
                                                        className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand sm:text-sm"
                                                >
                                                        Cancel
                                                </button>
                                                <button
                                                        type="button"
                                                        onClick={handleSave}
                                                        data-testid="ns-save"
                                                        disabled={saving}
                                                        className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-brand text-base font-medium text-white hover:bg-brand/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand sm:text-sm disabled:bg-gray-300"
                                                >
                                                        {saving ? 'Saving...' : 'Save'}
                                                </button>
                                        </div>
                                </div>
                        )}
                </Modal>
        );
};

export default NotificationsModal;
